// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Box, Group, Stack, Text } from '@mantine/core';
import type { Communication } from '@medplum/fhirtypes';
import { getNotificationBody, getNotificationSentAt, getNotificationTitle, isRead } from '@polarissleep/shared';
import type { JSX } from 'react';

interface NotificationDropdownItemProps {
  notification: Communication;
  onClick: (notification: Communication) => void;
}

function formatRelative(date: Date | undefined): string {
  if (!date) {
    return '';
  }
  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (seconds < 60) {
    return 'just now';
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  const days = Math.floor(hours / 24);
  if (days < 7) {
    return `${days}d ago`;
  }
  return date.toLocaleDateString();
}

export function NotificationDropdownItem({ notification, onClick }: NotificationDropdownItemProps): JSX.Element {
  const read = isRead(notification);
  const title = getNotificationTitle(notification);
  const body = getNotificationBody(notification);
  const sentAt = getNotificationSentAt(notification);

  return (
    <Box
      component="button"
      type="button"
      onClick={() => onClick(notification)}
      data-read={read ? 'true' : 'false'}
      style={{
        width: '100%',
        textAlign: 'left',
        cursor: 'pointer',
        background: 'transparent',
        border: 0,
        padding: 0,
      }}
    >
      <Group align="flex-start" gap="sm" wrap="nowrap" p="sm">
        <Box
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: read ? 'transparent' : 'var(--mantine-color-blue-6)',
            marginTop: 6,
            flexShrink: 0,
          }}
          aria-label={read ? 'Read' : 'Unread'}
        />
        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
          <Group justify="space-between" gap="xs" wrap="nowrap">
            <Text fw={read ? 400 : 600} c={read ? 'dimmed' : undefined} size="sm" lineClamp={1}>
              {title}
            </Text>
            <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
              {formatRelative(sentAt)}
            </Text>
          </Group>
          {body && (
            <Text size="sm" c="dimmed" lineClamp={2}>
              {body}
            </Text>
          )}
        </Stack>
      </Group>
    </Box>
  );
}
